import { yupResolver } from "@hookform/resolvers/yup";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControl, FormHelperText, IconButton, InputAdornment, InputLabel, OutlinedInput, Typography } from "@mui/material";
import { RiCloseLine, RiEyeLine, RiEyeOffLine } from "@remixicon/react";
import { useState, type FunctionComponent } from "react";
import { useForm, type SubmitHandler } from "react-hook-form";
import { ChangePasswordFormSchema } from "./ChangePasswordForm.schema";
import { useChangePassword } from "@/shared/hooks/useChangePassword.hook";
import customAlert from "../CustomAlert";
import { useUserData } from "@/shared/hooks/useUserData.hook";
import type { DataInitRegister } from "@/modules/login/components/CredencialesForm/CredencialesForm";

export interface ChangePasswordFormValues{
    currentPassword: string
    newPassword: string
    confirmPassword: string
}

interface ChangePasswordFormProps {
    closeModal: () => void
    isOpen: boolean
}

const ChangePasswordForm: FunctionComponent<ChangePasswordFormProps> = ({
    closeModal, isOpen
}) => {

    const { userData } = useUserData()

    const [showActualPassword, setShowActualPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const handleClickShowActualPassword = () => setShowActualPassword((show) => !show);
    const handleClickShowNewPassword = () => setShowNewPassword((show) => !show);
    const handleClickShowConfirmPassword = () => setShowConfirmPassword((show) => !show);

    const dataInitRegister : DataInitRegister = {
        apellido: userData?.fullName || '',
        cedula: userData?.identificacion || '',
        nombre: userData?.fullName || ''
    }

    const {
        register, handleSubmit, formState: { errors }, setValue, setError, reset
    } = useForm<ChangePasswordFormValues>({
        resolver: yupResolver(ChangePasswordFormSchema(dataInitRegister)),
        mode: "onChange",
        reValidateMode: "onChange"
    });

    const handleClose = () => {
        reset();
        closeModal();
    };

    const {
        mutate: changePassword, isPending: isPendingChangePassword
    } = useChangePassword();

    const onSubmit: SubmitHandler<ChangePasswordFormValues> = (data) => {
        console.log(data);

        customAlert({
            icon: "question",
            titulo: "¿Estás seguro(a) de realizar el cambio de contraseña? ",
            mensaje: "Se cerrarán todas tus sesiones abiertas.",
            botonCancelar: true,
            cerrarEsc: false,
            cerrarClicFuera: false,
            textoBotonAceptar: "Sí",
            colorBotonAceptar: '#3085d6',
            colorBotonCancelar: '#d33',
            textoBotonCancelar: "No",
            callback: (res) =>  {
                if (res) {
                    changePassword({
                        currentPassword: data.currentPassword, 
                        newPassword: data.newPassword
                    }, {
                        onSuccess: (res) => {
                            console.log(res)
                            closeModal();
                            reset();
                            customAlert({
                                icon: 'success',
                                titulo: `Cambio de contraseña realizado con éxito!`,
                                tiempo: 8,
                                toast: true,
                            });
                        },
                        onError: (err) => {
                            customAlert({
                                icon: 'error',
                                titulo: err.message || `Ha ocurrido un error al intentar cambiar de contraseña`,
                                tiempo: 8,
                                toast: true,
                            });
                        }
                    });
                }          
            }
        });
    }

    return (
        <>
        <Dialog
            open={isOpen}
            onClose={(_event, reason) => {
                if (reason === "backdropClick") return;                     
                handleClose();
            }}
            disableEscapeKeyDown
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    boxShadow: 24
                }
            }}
        >
            <DialogTitle>
                <div className="flex justify-between items-center">
                    <Typography variant="h5" component="div" fontWeight="600">
                        {"Cambio de contraseña"}
                    </Typography>
                    <IconButton onClick={handleClose} size="small">
                        <RiCloseLine />
                    </IconButton>
                </div>
            </DialogTitle>

            <Divider />
            
            <DialogContent className={`space-y-6`}>    
                <form className={`flex flex-col gap-6`}>
                    <FormControl variant="outlined" fullWidth>
                        <InputLabel htmlFor="outlined-adornment-password-actual">Contraseña Actual</InputLabel>
                        <OutlinedInput
                            id="outlined-adornment-password-actual"
                            type={showActualPassword ? 'text' : 'password'}
                            disabled={isPendingChangePassword}
                            endAdornment={
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label={
                                            showActualPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'
                                        }
                                        onClick={handleClickShowActualPassword}
                                        edge="end"
                                        disabled={isPendingChangePassword}
                                    >
                                        {showActualPassword ? <RiEyeOffLine /> : <RiEyeLine />}
                                    </IconButton>
                                </InputAdornment>
                            }
                            label="Contraseña Actual"
                            {...register('currentPassword')}
                            error={errors.currentPassword?.message ? true : false}
                        />
                        {
                            errors.currentPassword?.message
                            &&
                            <FormHelperText error>
                                {errors.currentPassword.message.toString()}
                            </FormHelperText>
                        }
                    </FormControl>
                    <FormControl variant="outlined" fullWidth>
                        <InputLabel htmlFor="outlined-adornment-password-nueva">Nueva Contraseña</InputLabel>
                        <OutlinedInput
                            id="outlined-adornment-password-nueva"
                            type={showNewPassword ? 'text' : 'password'}
                            disabled={isPendingChangePassword}
                            endAdornment={
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label={
                                            showNewPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'
                                        }
                                        onClick={handleClickShowNewPassword}
                                        edge="end"
                                        disabled={isPendingChangePassword}
                                    >
                                        {showNewPassword ? <RiEyeOffLine /> : <RiEyeLine />}
                                    </IconButton>
                                </InputAdornment>
                            }
                            label="Nueva Contraseña"
                            {...register('newPassword')}
                            error={errors.newPassword?.message ? true : false}
                        />
                        {
                            errors.newPassword?.message
                            &&
                            <FormHelperText error>
                                {errors.newPassword.message.toString()}
                            </FormHelperText>
                        }
                    </FormControl>
                    <FormControl variant="outlined" fullWidth>
                        <InputLabel htmlFor="outlined-adornment-password-confirmar">Confirmar Contraseña</InputLabel>
                        <OutlinedInput
                            id="outlined-adornment-password-confirmar"
                            type={showConfirmPassword ? 'text' : 'password'}
                            disabled={isPendingChangePassword}
                            endAdornment={
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label={
                                            showConfirmPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'
                                        }
                                        onClick={handleClickShowConfirmPassword}
                                        edge="end"
                                        disabled={isPendingChangePassword}
                                    >
                                        {showConfirmPassword ? <RiEyeOffLine /> : <RiEyeLine />}
                                    </IconButton>
                                </InputAdornment>
                            }
                            label="Confirmar Contraseña"
                            {...register('confirmPassword')}
                            error={errors.confirmPassword?.message ? true : false}
                        />
                        {
                            errors.confirmPassword?.message
                            &&
                            <FormHelperText error>
                                {errors.confirmPassword.message.toString()}
                            </FormHelperText>
                        }
                    </FormControl>
                </form>
            </DialogContent>

            <Divider />

            <DialogActions className="py-[0.8rem]! px-[1.5rem]!">
                <Button 
                    variant="outlined" 
                    color="error" 
                    onClick={handleClose}
                    disabled={isPendingChangePassword}
                >
                    Cancelar
                </Button>
                <Button 
                    variant="contained" 
                    color="primary" 
                    onClick={handleSubmit(onSubmit)} 
                    loadingPosition="end" 
                    loading={isPendingChangePassword}
                >
                    {!isPendingChangePassword ? "Guardar" : "Guardando"}
                </Button>
            </DialogActions>
        </Dialog>
        </>
    );
}

export default ChangePasswordForm;